(() => {
  if (window.MCImageLinkGridCarousel) return;

  const MOBILE_QUERY = '(max-width: 749px)';
  const AUTOPLAY_DELAY = 4000;
  const RESUME_DELAY = 6000;
  const instances = new WeakMap();

  class MCImageLinkGridCarousel {
    constructor(root) {
      this.root = root;
      this.track = root.querySelector('[data-mc-image-link-track]');
      this.pagination = root.querySelector('[data-mc-image-link-pagination]');
      this.dots = Array.from(root.querySelectorAll('[data-mc-image-link-dot]'));
      this.mediaQuery = window.matchMedia(MOBILE_QUERY);
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.originalSlides = this.track
        ? Array.from(this.track.querySelectorAll(':scope > [data-mc-image-link-slide]'))
        : [];

      this.active = false;
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.resumeTimer = null;
      this.scrollTimer = null;
      this.resizeTimer = null;
      this.cloneStart = null;
      this.cloneEnd = null;
      this.isJumping = false;

      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this.handlePointerDown = this.handlePointerDown.bind(this);
      this.handlePointerUp = this.handlePointerUp.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleResize = this.handleResize.bind(this);
      this.handleDotClick = this.handleDotClick.bind(this);

      if (!this.track || this.originalSlides.length < 2) return;

      this.mediaQuery.addEventListener?.('change', this.handleMediaChange);
      window.addEventListener('resize', this.handleResize, { passive: true });
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));

      this.handleMediaChange();
    }

    handleMediaChange() {
      if (this.mediaQuery.matches) {
        this.activate();
      } else {
        this.deactivate();
      }
    }

    activate() {
      if (this.active) return;
      this.active = true;

      this.cloneStart = this.createClone(this.originalSlides[this.originalSlides.length - 1]);
      this.cloneEnd = this.createClone(this.originalSlides[0]);
      this.track.prepend(this.cloneStart);
      this.track.append(this.cloneEnd);

      this.track.addEventListener('scroll', this.handleScroll, { passive: true });
      this.track.addEventListener('pointerdown', this.handlePointerDown, { passive: true });
      this.track.addEventListener('pointerup', this.handlePointerUp, { passive: true });
      this.track.addEventListener('pointercancel', this.handlePointerUp, { passive: true });
      this.track.addEventListener('touchend', this.handlePointerUp, { passive: true });

      requestAnimationFrame(() => {
        this.jumpToPosition(1);
        this.updateDots(0);
        this.startAutoplay();
      });
    }

    deactivate() {
      if (!this.active) return;
      this.active = false;

      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
      clearTimeout(this.scrollTimer);
      clearTimeout(this.resizeTimer);

      this.track.removeEventListener('scroll', this.handleScroll);
      this.track.removeEventListener('pointerdown', this.handlePointerDown);
      this.track.removeEventListener('pointerup', this.handlePointerUp);
      this.track.removeEventListener('pointercancel', this.handlePointerUp);
      this.track.removeEventListener('touchend', this.handlePointerUp);

      this.cloneStart?.remove();
      this.cloneEnd?.remove();
      this.cloneStart = null;
      this.cloneEnd = null;
      this.track.scrollLeft = 0;
    }

    createClone(slide) {
      const clone = slide.cloneNode(true);
      clone.dataset.mcImageLinkClone = 'true';
      clone.setAttribute('aria-hidden', 'true');
      clone.setAttribute('tabindex', '-1');
      clone.removeAttribute('data-shopify-editor-block');
      clone.querySelectorAll('[data-shopify-editor-block]').forEach((element) => {
        element.removeAttribute('data-shopify-editor-block');
      });
      return clone;
    }

    get slideWidth() {
      return this.track.clientWidth;
    }

    jumpToPosition(position) {
      if (!this.slideWidth) return;
      this.isJumping = true;
      const previousBehavior = this.track.style.scrollBehavior;
      this.track.style.scrollBehavior = 'auto';
      this.track.scrollLeft = position * this.slideWidth;
      this.track.offsetHeight;
      this.track.style.scrollBehavior = previousBehavior;
      requestAnimationFrame(() => {
        this.isJumping = false;
      });
    }

    scrollToIndex(index, behavior = 'smooth') {
      if (!this.active || !this.slideWidth) return;
      const normalizedIndex = (index + this.originalSlides.length) % this.originalSlides.length;
      this.currentIndex = normalizedIndex;
      this.updateDots(normalizedIndex);
      this.track.scrollTo({
        left: (normalizedIndex + 1) * this.slideWidth,
        behavior: this.reducedMotion.matches ? 'auto' : behavior,
      });
    }

    handleScroll() {
      if (!this.active || this.isJumping) return;
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.settleAfterScroll(), 90);
    }

    settleAfterScroll() {
      if (!this.active || !this.slideWidth) return;

      const position = Math.round(this.track.scrollLeft / this.slideWidth);
      const lastRealPosition = this.originalSlides.length;
      const cloneEndPosition = this.originalSlides.length + 1;

      if (position <= 0) {
        this.currentIndex = this.originalSlides.length - 1;
        this.updateDots(this.currentIndex);
        this.jumpToPosition(lastRealPosition);
        return;
      }

      if (position >= cloneEndPosition) {
        this.currentIndex = 0;
        this.updateDots(0);
        this.jumpToPosition(1);
        return;
      }

      this.currentIndex = position - 1;
      this.updateDots(this.currentIndex);
    }

    updateDots(index) {
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === index;
        dot.classList.toggle('is-active', isActive);
        if (isActive) {
          dot.setAttribute('aria-current', 'true');
        } else {
          dot.removeAttribute('aria-current');
        }
      });
    }

    handleDotClick(event) {
      const index = Number(event.currentTarget.dataset.mcImageLinkDot);
      if (!Number.isInteger(index)) return;
      this.pauseAndResume();
      this.scrollToIndex(index);
    }

    handlePointerDown() {
      this.stopAutoplay();
      clearTimeout(this.resumeTimer);
    }

    handlePointerUp() {
      this.scheduleResume();
    }

    pauseAndResume() {
      this.stopAutoplay();
      this.scheduleResume();
    }

    scheduleResume() {
      clearTimeout(this.resumeTimer);
      this.resumeTimer = setTimeout(() => this.startAutoplay(), RESUME_DELAY);
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.active || document.hidden || this.reducedMotion.matches) return;
      this.autoplayTimer = setInterval(() => {
        this.goNext();
      }, AUTOPLAY_DELAY);
    }

    goNext() {
      if (!this.active || !this.slideWidth) return;

      if (this.currentIndex === this.originalSlides.length - 1) {
        this.currentIndex = 0;
        this.updateDots(0);
        this.track.scrollTo({
          left: (this.originalSlides.length + 1) * this.slideWidth,
          behavior: this.reducedMotion.matches ? 'auto' : 'smooth',
        });
        return;
      }

      this.scrollToIndex(this.currentIndex + 1);
    }

    stopAutoplay() {
      clearInterval(this.autoplayTimer);
      this.autoplayTimer = null;
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
      }
    }

    handleResize() {
      if (!this.active) return;
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.jumpToPosition(this.currentIndex + 1);
      }, 120);
    }

    destroy() {
      this.deactivate();
      this.mediaQuery.removeEventListener?.('change', this.handleMediaChange);
      window.removeEventListener('resize', this.handleResize);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
    }
  }

  const initialize = (scope = document) => {
    scope.querySelectorAll('[data-mc-image-link-carousel]').forEach((root) => {
      if (instances.has(root)) return;
      const instance = new MCImageLinkGridCarousel(root);
      instances.set(root, instance);
    });
  };

  const destroy = (scope) => {
    scope.querySelectorAll('[data-mc-image-link-carousel]').forEach((root) => {
      const instance = instances.get(root);
      instance?.destroy();
      instances.delete(root);
    });
  };

  window.MCImageLinkGridCarousel = { initialize, destroy };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize());
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroy(event.target));
})();
